/******************************************************************************
 *
 * Copyright (c) 2019-2020 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Vishal Goel' <vishal@wakencode.com>, May 2020
 *
 *****************************************************************************/

import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import Typography from "@material-ui/core/Typography";
import Edit from "@material-ui/icons/Edit";
import Business from "@material-ui/icons/Business";
import Place from "@material-ui/icons/Place";
import Group from "@material-ui/icons/Group";
import Cancel from "@material-ui/icons/Cancel";
import Launch from "@material-ui/icons/Launch";
import Work from "@material-ui/icons/Work";
import { constant } from "./../../../shared/constants/constants";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Divider from "@material-ui/core/Divider";
import { useDispatch, useSelector } from "react-redux";
import { saveWorkOrderUser } from "./../../../store/actions";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import { toast } from "react-toastify";
import Tooltip from "./../../../Tooltip/Tooltip";
import NickNames from "./NickNames";

const Card = (props) => {
  const dispatch = useDispatch();
  const userList = useSelector((state) => state.user.users);
  const profileName = useSelector((state) => state.auth.profileName);
  const [user, setUser] = useState([]);

  const classes = useStyles();

  // handle any change happen in workorder user dropdown
  const handleChange = (event, workOrderId) => {
    // set user in state
    setUser((user) => {
      let updatedUser = cloneDeep(user);
      updatedUser[workOrderId] = event.target.value;
      return updatedUser;
    });

    // if user is selected (workorder assigned case)
    if (event.target.value) {
      // check selected user is present in user list
      let assignedWorkOrder = find(
        userList,
        (u) => u.userId === event.target.value
      );
      // if present in list call api to save the user else show error toast
      if (assignedWorkOrder) {
        dispatch(saveWorkOrderUser(workOrderId, assignedWorkOrder.id));
      } else {
        toast.error(constant.TECHNICIAN_NOT_FOUND);
      }
    } else {
      // if user is not selected (workorder unassigned case)
      dispatch(saveWorkOrderUser(workOrderId, null));
    }
  };

  // property select helper method to avoid user to select same property card again
  const propertySelectHandler = () => {
    if (props.selectedPropertyId === props.info.propertyId) return;
    props.setProperty(props.info);
  };

  // show edit icon if edit mode is not selected
  let iconRender = (
    <span
      style={{ color: "grey" }}
      onClick={() => props.setButtonVisibilityStatus(props.info.propertyId)}>
      <Edit color="inherit" />
    </span>
  );

  // show cancel icon if edit mode is selected
  if (props.editButton === props.info.propertyId) {
    iconRender = (
      <span
        style={{ color: "grey" }}
        onClick={() => props.setButtonVisibilityStatus(null)}>
        <Cancel color="inherit" />
      </span>
    );
  }
  // don't show edit mode when profile is not admin
  if (profileName && !profileName.toLowerCase().includes("admin"))
    iconRender = null;

  // don't show edit mode when property is partnered
  if (props.info.isPartner) iconRender = null;

  // dynamic changes css class
  let expansionPanelClass = classes.panel;
  if (props.selected) expansionPanelClass = classes.selectedPanel;
  if (props.editButton !== props.info.propertyId && props.editButton)
    expansionPanelClass = classes.nonEdit;

  return (
    <div className={classes.root}>
      <ExpansionPanel
        expanded={props.selected}
        TransitionProps={{ unmountOnExit: true }}
        className={expansionPanelClass}>
        <ExpansionPanelSummary
          aria-controls="panel1a-content"
          id="panel1a-header"
          classes={{
            root: classes.panelSummary,
            content: classes.panelContent,
            expanded: classes.expandedSummary,
          }}>
          <div style={{ display: "flex" }} onClick={propertySelectHandler}>
            <span style={{ color: "grey" }}>
              <Business color="inherit" />
            </span>
            <Tooltip title={props.info.propertyName} arrow>
              <Typography className={classes.type}>
                {props.info.propertyName}
              </Typography>
            </Tooltip>
          </div>
          <div className={classes.flexCenter}>
            <NickNames workOrders={props.info.workOrders} />
            <Tooltip
              title={constant.CLICK_TO_SEE_PROPERTY_IN_SERVICE_ACTIVATOR}
              arrow>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`${process.env.REACT_APP_MSA_URL}${constant.PROPERTY_URL}/${props.info.propertyId}`}
                style={{ color: "grey", marginLeft: "8px" }}>
                <Launch color="inherit" />
              </a>
            </Tooltip>
          </div>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.panelDetails}>
          <div
            className={classes.flexCenter}
            style={{ justifyContent: "space-between" }}>
            <div className={classes.flexCenter}>
              <span style={{ color: "grey" }}>
                <Group color="inherit" />
              </span>
              <Typography className={classes.type1}>
                {props.info.customerName}
              </Typography>
            </div>

            {iconRender}
          </div>

          {props.info.address && (
            <div className={classes.flexCenter}>
              <Tooltip style={{ color: "grey" }} title={constant.ADDRESS} arrow>
                <Place color="inherit" />
              </Tooltip>
              <Typography className={classes.address}>
                {props.info.address}
              </Typography>
            </div>
          )}

          <div
            style={{
              marginTop: props.info.workOrders.length > 0 ? "16px" : 0,
            }}>
            {props.info.workOrders.map((w) => (
              <React.Fragment key={w.workOrderId}>
                <div
                  className={classes.workInnerCont}
                  style={{ marginBottom: "4px" }}>
                  <div className={classes.flexCenter}>
                    <Tooltip
                      style={{ color: "grey" }}
                      title={constant.WORK_ORDER}
                      arrow>
                      <Work color="inherit" />
                    </Tooltip>
                    <Tooltip
                      title={
                        constant.CLICK_TO_SEE_WORKORDER_IN_SERVICE_ACTIVATOR
                      }
                      arrow>
                      <a
                        className={classes.workName}
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${process.env.REACT_APP_MSA_URL}${constant.WORK_ORDER_URL}/${w.workOrderId}`}>
                        {w.workOrderName}
                      </a>
                    </Tooltip>
                  </div>

                  {w.userType === "technician" ? (
                    <FormControl className={classes.contName}>
                      <Select
                        classes={{
                          root: classes.select,
                        }}
                        disableUnderline
                        displayEmpty
                        value={
                          user[w.workOrderId] || user[w.workOrderId] === ""
                            ? user[w.workOrderId]
                            : w.userNameId
                        }
                        onChange={(e) => handleChange(e, w.workOrderId)}>
                        <MenuItem value="">
                          {constant.ASSIGN_TECHNICIAN}
                        </MenuItem>
                        {userList.map((user) => (
                          <MenuItem key={user.userId} value={user.userId}>
                            {user.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}

                  {!w.userNameId ? (
                    <FormControl className={classes.contName}>
                      <Select
                        classes={{
                          root: classes.select,
                        }}
                        disableUnderline
                        displayEmpty
                        value={user[w.workOrderId] ? user[w.workOrderId] : ""}
                        onChange={(e) => handleChange(e, w.workOrderId)}>
                        <MenuItem value="">
                          {constant.ASSIGN_TECHNICIAN}
                        </MenuItem>
                        {userList.map((user) => (
                          <MenuItem key={user.userId} value={user.userId}>
                            {user.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}

                  {w.userType === "contractor" ? (
                    <FormControl className={classes.contName}>
                      <Select
                        classes={{
                          root: classes.select,
                          icon: classes.hideSelectIcon,
                        }}
                        disableUnderline
                        readOnly
                        value={w.userNameId}>
                        <MenuItem value={w.userNameId}>{w.userName}</MenuItem>
                      </Select>
                    </FormControl>
                  ) : null}
                </div>
                {props.info.workOrders.length > 1 && <Divider />}
              </React.Fragment>
            ))}
          </div>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  nonEdit: {
    backgroundColor: "#e8e8e8",
    marginBottom: "12px !important",
  },

  panel: {
    marginBottom: "12px !important",
    borderRadius: "0 !important",
  },
  selectedPanel: {
    boxShadow: "3px 2px 5px 2px #8fa225",
    marginBottom: "12px !important",
    borderRadius: "0 !important",
  },
  panelDetails: {
    flexDirection: "column",
    textAlign: "left",
    padding: "0px 12px 12px",
  },
  panelSummary: {
    padding: "0px 12px 0px 12px",
    height: "50px !important",
    minHeight: "50px !important",
  },
  expandedSummary: {
    height: "50px !important",
    minHeight: "50px !important",
  },
  panelContent: {
    justifyContent: "space-between",
    alignItems: "center",
  },
  twoLetter: {
    borderRadius: " 50%",
    height: "40px",
    width: "40px",
    fontSize: "18px",
    color: "#fff",
    marginRight: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  type: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginLeft: "8px",
    maxWidth: "235px",
    minWidth: "120px",
    textAlign: "left",
  },
  type1: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginLeft: "8px",
    fontSize: "0.9em",
    width: "235px",
  },
  address: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginLeft: "8px",
    fontSize: "0.9em",
    width: "270px",
  },
  flexCenter: {
    display: "flex",
    alignItems: "center",
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
  workCont: {
    marginTop: "16px",
  },
  workInnerCont: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  workName: {
    marginLeft: "8px",
    fontSize: "0.9em",
    color: "grey",
  },
  contName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.9em",
    width: "160px",
  },
  hideSelectIcon: {
    display: "none",
  },
  select: {
    fontSize: "14px",
  },
  errorIcon: {
    color: "grey",
    marginRight: "8px",
    fontSize: "35px",
  },
  warningIcon: {
    color: "#F28E1A",
    marginRight: "8px",
    fontSize: "35px",
  },
}));

export default Card;
